import { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { nextSquadId, squadsWithIds } from '../utils/teamFixtures';

const GENDERS   = ['Male', 'Female', 'Mixed'];
const ABILITIES = ['Low', 'Medium', 'High'];
const JERSEY_PATTERNS = [
  { id: 'plain',   label: 'Plain' },
  { id: 'stripes', label: 'Stripes' },
  { id: 'hoops',   label: 'Hoops' },
  { id: 'halves',  label: 'Half & Half' },
];
const KIT_COLOURS = [
  { name: 'White',    hex: '#FFFFFF', border: true },
  { name: 'Black',    hex: '#111827' },
  { name: 'Grey',     hex: '#6B7280' },
  { name: 'Red',      hex: '#DC2626' },
  { name: 'Blue',     hex: '#1D4ED8' },
  { name: 'Yellow',   hex: '#FBBF24' },
  { name: 'Green',    hex: '#16A34A' },
  { name: 'Orange',   hex: '#EA580C' },
  { name: 'Navy',     hex: '#1e3a5f' },
  { name: 'Maroon',   hex: '#7f1d1d' },
  { name: 'Sky Blue', hex: '#38bdf8' },
  { name: 'Purple',   hex: '#7c3aed' },
  { name: 'Pink',     hex: '#ec4899' },
];

function colourHex(name) {
  if (!name) return null;
  if (name.startsWith('#')) return name;
  return KIT_COLOURS.find((c) => c.name.toLowerCase() === name.toLowerCase())?.hex || null;
}

function YearsOfBirthInput({ values, onChange }) {
  const [inputVal, setInputVal] = useState('');

  function addYear() {
    const y = inputVal.trim();
    const n = Number(y);
    if (!y || isNaN(n) || n < 1990 || n > 2030) return;
    if (values.includes(y)) { setInputVal(''); return; }
    onChange([...values, y].sort((a, b) => a - b));
    setInputVal('');
  }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="number" min="1990" max="2030"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYear(); } }}
          placeholder="e.g. 2010"
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
        />
        <button type="button" onClick={addYear}
          className="px-3 py-1.5 bg-pb text-white text-[12px] font-semibold rounded-[8px] hover:bg-navy transition">
          Add
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((y) => (
            <span key={y} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[12px] font-semibold text-pb">
              {y}
              <button type="button" onClick={() => onChange(values.filter((v) => v !== y))}
                className="text-[#93C5FD] hover:text-pb transition leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorPicker({ value, onChange, label }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {KIT_COLOURS.map((c) => (
          <button
            key={c.name}
            type="button"
            title={c.name}
            onClick={() => onChange(c.name)}
            className={`w-7 h-7 rounded-[6px] transition-all ${
              value?.toLowerCase() === c.name.toLowerCase()
                ? 'ring-2 ring-offset-1 ring-pb scale-110'
                : 'hover:scale-110'
            } ${c.border ? 'border border-[#D1D5DB]' : ''}`}
            style={{ background: c.hex }}
          />
        ))}
        <input
          type="color"
          title="Custom colour"
          value={colourHex(value) || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 rounded-[6px] cursor-pointer border border-[#E5E7EB] p-0"
        />
      </div>
      {value && (
        <p className="text-[11px] text-[#6B7280] mt-1">
          <span className="inline-block w-3 h-3 rounded-sm mr-1 border border-[#E5E7EB] align-middle" style={{ background: colourHex(value) || value }} />
          {value}
        </p>
      )}
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────

function Section({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E7EB] rounded-[10px] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition text-left"
      >
        <span className="text-[11px] font-bold text-[#374151] uppercase tracking-wider flex items-center gap-2">
          {title}
          {badge != null && badge > 0 && (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-pb text-white text-[9px] font-bold">
              {badge}
            </span>
          )}
        </span>
        <svg className={`w-4 h-4 text-[#9CA3AF] transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-4 border-t border-[#E5E7EB]">{children}</div>}
    </div>
  );
}

// ── Squad editor ──────────────────────────────────────────────────────────────

function SquadEditor({ sq, idx, onChange }) {
  function set(field, val) { onChange({ ...sq, [field]: val }); }

  function setPref(field, val) {
    set('preferredFixture', { ...(sq.preferredFixture || {}), [field]: val });
  }

  return (
    <div className="border border-[#E5E7EB] rounded-[12px] overflow-hidden">
      <div className="bg-[#F9FAFB] px-4 py-2.5 border-b border-[#E5E7EB]">
        <input
          value={sq.label || ''}
          onChange={(e) => set('label', e.target.value)}
          className="w-full bg-transparent text-[13px] font-bold text-[#111827] focus:outline-none"
          placeholder={`Squad ${idx + 1}`}
        />
      </div>
      <div className="p-4 space-y-4">

        {/* Squad details */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Gender</label>
            <select value={sq.gender || ''} onChange={(e) => set('gender', e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              <option value="">—</option>
              {GENDERS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Years of Birth</label>
            <YearsOfBirthInput
              values={Array.isArray(sq.yearsOfBirth) ? sq.yearsOfBirth : (sq.yearOfBirth ? [sq.yearOfBirth] : [])}
              onChange={(years) => set('yearsOfBirth', years)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Players / Match</label>
            <input
              type="number" min="1" max="15"
              value={sq.playersPerMatch || ''}
              onChange={(e) => set('playersPerMatch', e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Ability</label>
            <select value={sq.abilityLevel || ''} onChange={(e) => set('abilityLevel', e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
              <option value="">—</option>
              {ABILITIES.map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* Home kit */}
        <Section title="Home Kit">
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Jersey Pattern</p>
              <div className="flex gap-2 flex-wrap">
                {JERSEY_PATTERNS.map((p) => (
                  <button key={p.id} type="button"
                    onClick={() => set('homeJerseyPattern', p.id)}
                    className={`px-2.5 py-1 rounded-[6px] text-[11px] font-semibold border transition ${sq.homeJerseyPattern === p.id ? 'bg-pb text-white border-pb' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-pb'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <ColorPicker label="Jersey" value={sq.homeJerseyColour} onChange={(v) => set('homeJerseyColour', v)} />
            {sq.homeJerseyPattern !== 'plain' && (
              <ColorPicker label="Jersey 2nd Colour" value={sq.homeJerseyColour2} onChange={(v) => set('homeJerseyColour2', v)} />
            )}
            <ColorPicker label="Shorts" value={sq.homeShortsColour} onChange={(v) => set('homeShortsColour', v)} />
            <ColorPicker label="Socks"  value={sq.homeSocksColour}  onChange={(v) => set('homeSocksColour', v)} />
          </div>
        </Section>

        {/* Only one kit toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!sq.onlyOneKit} onChange={(e) => set('onlyOneKit', e.target.checked)}
            className="w-4 h-4 rounded border-[#D1D5DB] accent-pb" />
          <span className="text-[13px] text-[#374151]">Home kit only (no away kit)</span>
        </label>

        {/* Away kit */}
        {!sq.onlyOneKit && (
          <Section title="Away Kit">
            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">Jersey Pattern</p>
                <div className="flex gap-2 flex-wrap">
                  {JERSEY_PATTERNS.map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => set('awayJerseyPattern', p.id)}
                      className={`px-2.5 py-1 rounded-[6px] text-[11px] font-semibold border transition ${sq.awayJerseyPattern === p.id ? 'bg-pb text-white border-pb' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-pb'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <ColorPicker label="Jersey" value={sq.awayJerseyColour} onChange={(v) => set('awayJerseyColour', v)} />
              {sq.awayJerseyPattern !== 'plain' && (
                <ColorPicker label="Jersey 2nd Colour" value={sq.awayJerseyColour2} onChange={(v) => set('awayJerseyColour2', v)} />
              )}
              <ColorPicker label="Shorts" value={sq.awayShortsColour} onChange={(v) => set('awayShortsColour', v)} />
              <ColorPicker label="Socks"  value={sq.awaySocksColour}  onChange={(v) => set('awaySocksColour', v)} />
            </div>
          </Section>
        )}

        {/* Preferred fixture (fixture finder) */}
        <Section title="Preferred Fixture (Fixture Finder)">
          <div className="space-y-2">
            <p className="text-[11px] text-[#6B7280]">
              Fill this in to export a fixture request for sharing on WhatsApp / messaging with other coaches.
            </p>
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Preferred Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={sq.preferredFixture?.preferredDate
                    ? `${sq.preferredFixture.preferredDate}T${sq.preferredFixture.preferredTime || '00:00'}`
                    : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {
                      set('preferredFixture', { ...(sq.preferredFixture || {}), preferredDate: '', preferredTime: '' });
                      return;
                    }
                    set('preferredFixture', { ...(sq.preferredFixture || {}), preferredDate: val.slice(0, 10), preferredTime: val.slice(11) });
                  }}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Preferred Location</label>
                <input type="text" value={sq.preferredFixture?.preferredLocation || ''} onChange={(e) => setPref('preferredLocation', e.target.value)}
                  placeholder="e.g. Heron Eccles, L18 7JT"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
              </div>
            </div>
          </div>
        </Section>

        <p className="text-[11.5px] text-[#9CA3AF]">
          Confirmed fixtures for this squad's team are added in the Fixtures section of this tab.
        </p>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function TeamInfoEditPanel({ requestId, teamInfo, onSave, onCancel }) {
  const [data, setData]     = useState(() => ({
    numSquads:        teamInfo.numSquads || 1,
    goalkeepersTotal: teamInfo.goalkeepersTotal ?? '',
    squads:           squadsWithIds(teamInfo.squads).map((s) => ({ ...s })),
  }));
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  function setField(field, val) {
    setData((p) => ({ ...p, [field]: val }));
  }

  function handleNumSquadsChange(n) {
    const count = Number(n);
    setData((p) => {
      const existing = p.squads.slice(0, count);
      while (existing.length < count) {
        existing.push({
          id: nextSquadId(existing),
          label: `Squad ${existing.length + 1}`,
          gender: '', yearsOfBirth: [], playersPerMatch: '', abilityLevel: '',
          onlyOneKit: false,
          homeJerseyColour: '', homeJerseyColour2: '', homeJerseyPattern: 'plain',
          homeShortsColour: '', homeSocksColour: '',
          awayJerseyColour: '', awayJerseyColour2: '', awayJerseyPattern: 'plain',
          awayShortsColour: '', awaySocksColour: '',
          preferredFixture: {},
        });
      }
      return { ...p, numSquads: count, squads: existing };
    });
  }

  function updateSquad(idx, updated) {
    setData((p) => {
      const squads = [...p.squads];
      squads[idx] = updated;
      return { ...p, squads };
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...teamInfo,
        numSquads:        data.numSquads,
        goalkeepersTotal: data.goalkeepersTotal !== '' ? Number(data.goalkeepersTotal) : null,
        squads:           data.squads.slice(0, data.numSquads),
        updatedAt:        serverTimestamp(),
      };
      await setDoc(doc(db, 'requests', requestId, 'teamInfo', 'data'), payload, { merge: true });
      onSave(payload);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <h3 className="text-[14.5px] font-bold text-[#111827]">Edit Team Information</h3>
          <div className="flex gap-2">
            <button onClick={onCancel} disabled={saving}
              className="px-3 py-1.5 text-[12px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-[#F9FAFB] transition">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
        {error && <p className="px-[18px] py-2 text-[12px] text-dr bg-[#FEF2F2]">{error}</p>}
        <div className="px-[18px] py-[16px] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Number of squads</label>
              <select value={data.numSquads} onChange={(e) => handleNumSquadsChange(e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white">
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Total Goalkeepers</label>
              <input type="number" min="0"
                value={data.goalkeepersTotal}
                onChange={(e) => setField('goalkeepersTotal', e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Per-squad editors */}
      {data.squads.slice(0, data.numSquads).map((sq, i) => (
        <SquadEditor key={i} sq={sq} idx={i} onChange={(updated) => updateSquad(i, updated)} />
      ))}
    </div>
  );
}
